<template>
  <div
    :class="[
      'TabsCmp',
      {
        'with-tab-pic':
          dataTabs.find((e) => e.desktopImage || e.mobileImage) !== undefined,
      },
    ]"
  >
    <scroll-x-navbar-cmp
      v-if="dataTabs.length > 1 && showHeaders"
      class="tabs"
      role="tablist"
      :data-active-tab-id="`${_uid}-tab-${activeTabId}`"
      :data-navigation-enabled="hideNavigation"
      :data-navigation-always-visible-when-enabled="false"
      data-ul-role="none"
      @navigation="activeTabId = $event.replace(`${_uid}-tab-`, '')"
      @is-overflow-x="hideNavigation = $event"
    >
      <template #prev-icon>
        <svg>
          <use href="#icon-caret-left" />
        </svg>
      </template>
      <template #default>
        <li
          v-for="tab in dataTabs"
          :id="`${_uid}-tab-${tab.id}`"
          :key="`${_uid}-tab-${tab.id}`"
          :class="['tab', { active: tab.id === activeTabId }]"
          role="none"
        >
          <button
            ref="$tab"
            class="tab-label"
            type="button"
            role="tab"
            :tabindex="tab.id !== activeTabId ? '-1' : null"
            :aria-selected="tab.id === activeTabId"
            :aria-controls="`${_uid}-tabpanel-${tab.id}`"
            @click="tab.id !== activeTabId ? onTabChange(tab.id) : null"
            @keydown="handleKeydown($event, tab.id)"
          >
            <span v-if="tab.desktopImage || tab.mobileImage" class="figure">
              <image-cmp
                width="135"
                height="144"
                :data-desktop-image="tab.desktopImage"
                :data-mobile-image="tab.mobileImage"
                :data-image-alt="
                  tab.desktopImageAlt
                    ? tab.desktopImageAlt
                    : tab.mobileImageAlt
                    ? tab.mobileImageAlt
                    : ''
                "
              />
              <span class="a-paragraph-3">
                {{ tab.label }}
              </span>
            </span>
            <span v-else class="a-paragraph-3">{{ tab.label }}</span>
          </button>
        </li>
      </template>
      <template #next-icon>
        <svg>
          <use href="#icon-caret-right" />
        </svg>
      </template>
    </scroll-x-navbar-cmp>
    <div v-if="!dataNoContent" class="tabs-content" role="none">
      <transition name="fade">
        <div
          v-for="tab in activeTab"
          :key="`${_uid}-tab-content-${tab.id}`"
          class="tab-content"
          role="none"
        >
          <div
            :id="`${_uid}-tabpanel-${tab.id}`"
            role="tabpanel"
            :aria-labelledby="`${_uid}-tab-${tab.id}`"
            class="tabpanel"
            tabindex="0"
          >
            <slot :name="`tab-${tab.id}-content`" />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollXNavbarCmp from './scroll-x-navbar-cmp';
import ImageCmp from './image-cmp';
import { elementFocus } from '@/helpers/a11y';
import { castPropToType } from '@/helpers/cms-support';

export default {
  name: 'TabsCmp',
  components: {
    ImageCmp,
    ScrollXNavbarCmp,
  },
  props: {
    dataTabs: {
      type: Array,
      required: true,
    },
    dataActiveTabId: {
      type: String,
      default: '1',
    },
    dataNoContent: {
      type: Boolean,
      default: false,
    },
    dataRouting: {
      type: Boolean,
      default: false,
    },
    dataShowHeaders: {
      type: [Boolean, String],
      default: true,
    },
  },
  setup() {
    return {
      castPropToType,
    };
  },
  data() {
    return {
      activeTabId: null,
      hideNavigation: null,
    };
  },
  computed: {
    showHeaders() {
      return typeof this.dataShowHeaders === 'boolean'
        ? this.dataShowHeaders
        : this.castPropToType(this.dataShowHeaders, 'boolean');
    },
    activeTab() {
      return this.dataTabs.filter((e) => e.id === this.activeTabId);
    },
  },
  watch: {
    dataActiveTabId: {
      handler(val) {
        if (val) {
          this.activeTabId = this.dataActiveTabId;
        }
      },
      immediate: true,
    },
  },
  methods: {
    onTabChange(tabId) {
      if (this.dataNoContent) {
        this.$emit('update:data-active-tab-id', tabId);
      } else if (this.dataRouting) {
        this.$emit('update', tabId);
      } else {
        this.activeTabId = tabId;
      }
    },
    handleKeydown(event, activeTabId) {
      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Home' ||
        event.key === 'End'
      ) {
        const idx = this.dataTabs.findIndex((e) => e.id === activeTabId);

        if (idx !== -1) {
          event.stopPropagation();
          event.preventDefault();

          let newId;
          if (event.key === 'ArrowLeft') {
            const prevIdx =
              (idx - 1 + this.dataTabs.length) % this.dataTabs.length;
            newId = this.dataTabs[prevIdx].id;
          } else if (event.key === 'ArrowRight') {
            const nextIdx = (idx + 1) % this.dataTabs.length;
            newId = this.dataTabs[nextIdx].id;
          } else if (event.key === 'Home') {
            newId = this.dataTabs[0].id;
          } else if (event.key === 'End') {
            newId = this.dataTabs[this.dataTabs.length - 1].id;
          }
          this.onTabChange(newId);
          this.$nextTick(() => {
            this.$refs.$tab &&
              Array.isArray(this.$refs.$tab) &&
              elementFocus(
                this.$refs.$tab.find(
                  (e) => e.parentElement.id === `${this._uid}-tab-${newId}`
                )
              );
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.TabsCmp {
  &.with-tab-pic {
    margin-top: -136px;

    @include media-breakpoint-down(md) {
      padding-top: 64px;
    }

    @include media-breakpoint-up(lg) {
      margin-top: -176px;
    }
  }

  .tab-label {
    outline: none !important;
    outline-offset: 0 !important;

    &:focus-visible {
      span {
        outline: 3px solid $color-outline !important;
        outline-offset: 1px !important;
      }
    }
  }

  /* if there is a .tabs then set this animations rules */
  .tabs + .tabs-content {
    padding-top: 40px;

    @include media-breakpoint-up(md) {
      padding-top: 64px;
    }

    @include media-breakpoint-up(lg) {
      padding-top: 80px;
    }

    .fade-enter-active {
      transition: opacity 0.6s ease-in;
      transition-delay: 0.3s;
    }

    .fade-leave-active {
      transition: opacity 0.3s ease-out;
    }

    .fade-enter,
    .fade-leave-to {
      opacity: 0;
    }
  }

  .tabs-content {
    display: flex;
    justify-content: flex-end; /* fix jerky movements of the contents */
    width: 100%;
    overflow: hidden; /* fix jerky movements of the contents */

    .tab-content {
      width: 100%;
      flex: 1 0 100%;

      .tabpanel {
        &:focus-visible {
          outline-offset: -3px !important;
        }
      }
    }
  }

  .ScrollXNavbarCmp {
    --scroll-x-navbar-width: auto;
    --scroll-x-navbar-max-width: 100%;
    --scroll-x-navbar-spacing-x: 0px;
    --scroll-x-navbar-border-bottom-color: #{$color-grey-400};
    --scroll-x-navbar-button-height: calc(100% - 6px);
    --scroll-x-navbar-button-color: #{$color-blue};
    --scroll-x-navbar-button-icon-width: 1.5rem;
    --scroll-x-navbar-button-icon-height: 1.5rem;
    --scroll-x-navbar-tab-bg-color: transparent;
    --scroll-x-navbar-tab-color: #{$color-grey-800};
    --scroll-x-navbar-tab-active-color: #{$color-black};
    --scroll-x-navbar-tab-hover-color: #{$color-light-blue};
    --scroll-x-navbar-tab-padding: 4px;

    ::v-deep .button-prev,
    ::v-deep .button-next {
      top: 0;
      transform: none;
      padding: 3px;

      &:focus-visible {
        outline-offset: -3px !important;
      }
    }

    ::v-deep .button-prev {
      justify-content: flex-start;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 3rem;
        background: linear-gradient(
          to right,
          #ffffff 0%,
          #ffffff 34%,
          transparent 100%
        );
        z-index: map-get($z, under);
      }
    }

    ::v-deep .button-next {
      justify-content: flex-end;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        width: 3rem;
        background: linear-gradient(
          to left,
          #ffffff 0%,
          #ffffff 34%,
          transparent 100%
        );
        z-index: map-get($z, under);
      }
    }

    ::v-deep ul {
      align-items: normal;

      li.tab {
        --tabs-tab-padding-bottom: 4px;
        display: flex;
        align-items: flex-end;
        flex: 1 0 130px;
        min-width: auto !important;
        height: auto;
        padding: 0 0 var(--tabs-tab-padding-bottom) 0;
        margin-bottom: 1px;
        border-bottom: 1px solid transparent;
        text-align: center;
        white-space: normal;
        z-index: map-get($z, base);

        button.tab-label {
          display: flex;
          width: 100%;
          height: calc(100% - var(--tabs-tab-padding-bottom));
          text-align: center;
          transition: color 0.3s;

          span {
            width: 100%;
            height: 100%;
            align-content: flex-end;
          }
        }

        &.active {
          border-color: $color-blue;
        }

        .figure {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          height: 100%;

          img {
            width: auto;
            max-width: 100%;
            height: 120px;
            margin-bottom: 16px;
            opacity: 0;
            transform: translateY(10px);
            transition: opacity 0.6s, transform 1s;

            @include media-breakpoint-up(md) {
              height: 144px;
            }

            @include media-breakpoint-up(lg) {
              margin-bottom: 32px;
            }
          }
        }

        &.active,
        &:hover:not(.active) {
          .figure {
            img {
              opacity: 1;
              transform: translateY(0);
            }
          }
        }
      }
    }
  }
}
</style>
